// Stock sheet table handler functions

import { filterNumericInput, validateNumericField, validateStockSheet } from './validationUtils';

/**
 * Handle stock sheet field change
 * @param {number} idx - Stock sheet index
 * @param {string} field - Field name
 * @param {string} value - New value
 * @param {Array} stockSheets - Current stock sheets array
 * @param {Function} setStockSheets - State setter for stock sheets
 * @param {Array} panels - Current panels array
 * @param {Function} setPanels - State setter for panels
 * @param {Object} invalidStockFields - Current invalid fields object
 * @param {Function} setInvalidStockFields - State setter for invalid fields
 */
export const handleStockChange = (idx, field, value, stockSheets, setStockSheets, panels, setPanels, invalidStockFields, setInvalidStockFields) => {
  // When material type changes in stock sheets, update panels with same old material type
  if (field === 'materialType') {
    const oldMaterialType = stockSheets[idx]?.materialType || '';
    const newMaterialType = value;

    // Update all panels with the old material type to use the new material type
    if (oldMaterialType !== newMaterialType && oldMaterialType !== '') {
      const updatedPanels = panels.map(panel =>
        panel.materialType === oldMaterialType
          ? { ...panel, materialType: newMaterialType }
          : panel
      );
      setPanels(updatedPanels);
    }
  }

  // Filter input for numeric fields to only allow valid characters
  let filteredValue = value;
  if (['width', 'length', 'quantity'].includes(field)) {
    filteredValue = filterNumericInput(value, field);
  }

  // Update state with filtered value
  const updated = stockSheets.map((row, i) => i === idx ? { ...row, [field]: filteredValue } : row);
  setStockSheets(updated);

  // Only validate if value is clearly invalid (not empty, but invalid)
  // Empty values will be validated on blur
  if (['width', 'length', 'quantity'].includes(field)) {
    const fieldKey = `${idx}-${field}`;
    const currentStock = stockSheets[idx];
    
    // Allow empty values during typing
    if (filteredValue === '' || filteredValue === null) {
      // Don't mark as invalid during typing - will validate on blur
      return;
    }
    
    // Use centralized validation function
    const validation = validateNumericField(filteredValue, field, currentStock.enabled);

    // Update invalid fields tracking only for clearly invalid values
    setInvalidStockFields(prev => {
      if (validation.isValid) {
        // Remove from invalid list
        const newInvalid = { ...prev };
        delete newInvalid[fieldKey];
        return newInvalid;
      } else {
        // Add to invalid list
        return { ...prev, [fieldKey]: true };
      }
    });
  }
};

/**
 * Handle stock sheet field blur event
 * @param {number} idx - Stock sheet index
 * @param {string} field - Field name
 * @param {Array} stockSheets - Current stock sheets array
 * @param {Object} invalidStockFields - Current invalid fields object
 * @param {Function} setInvalidStockFields - State setter for invalid fields
 */
export const handleStockBlur = (idx, field, stockSheets, invalidStockFields, setInvalidStockFields) => {
  // Validate on blur - if enabled and empty, mark as invalid
  const stock = stockSheets[idx];
  const fieldKey = `${idx}-${field}`;
  
  if (['width', 'length', 'quantity'].includes(field) && stock.enabled) {
    const value = stock[field];
    
    // Use centralized validation function
    const validation = validateNumericField(value, field, stock.enabled);

    setInvalidStockFields(prev => {
      if (validation.isValid) {
        const newInvalid = { ...prev };
        delete newInvalid[fieldKey];
        return newInvalid;
      } else {
        return { ...prev, [fieldKey]: true };
      }
    });
  }
};

/**
 * Toggle stock sheet enabled state
 * @param {number} idx - Stock sheet index
 * @param {Array} stockSheets - Current stock sheets array
 * @param {Function} setStockSheets - State setter for stock sheets
 * @param {Object} invalidStockFields - Current invalid fields object
 * @param {Function} setInvalidStockFields - State setter for invalid fields
 */
export const toggleStockEnabled = (idx, stockSheets, setStockSheets, invalidStockFields, setInvalidStockFields) => {
  const updated = stockSheets.map((row, i) => i === idx ? { ...row, enabled: !row.enabled } : row);
  setStockSheets(updated);
  
  // Re-validate fields when enabled state changes
  const stock = updated[idx];
  if (stock.enabled) {
    // Stock sheet is now enabled - validate all required fields using centralized validation
    const stockValidation = validateStockSheet(stock, idx);
    const newInvalid = { ...invalidStockFields };
    
    // Update invalid fields based on validation result
    stockValidation.invalidFields.forEach(fieldKey => {
      newInvalid[fieldKey] = true;
    });
    
    // Remove fields that are now valid
    ['width', 'length', 'quantity'].forEach(field => {
      const fieldKey = `${idx}-${field}`;
      if (!stockValidation.invalidFields.includes(fieldKey)) {
        delete newInvalid[fieldKey];
      }
    });
    
    setInvalidStockFields(newInvalid);
  } else {
    // Stock sheet is now disabled - remove validation errors for this stock sheet
    const newInvalid = { ...invalidStockFields };
    ['width', 'length', 'quantity'].forEach(field => {
      delete newInvalid[`${idx}-${field}`];
    });
    setInvalidStockFields(newInvalid);
  }
};

/**
 * Toggle stock sheet grain direction
 * @param {number} idx - Stock sheet index
 * @param {Array} stockSheets - Current stock sheets array
 * @param {Function} setStockSheets - State setter for stock sheets
 */
export const toggleStockGrain = (idx, stockSheets, setStockSheets) => {
  const updated = stockSheets.map((row, i) => {
    if (i === idx) {
      const grainCycle = { either: 'horizontal', horizontal: 'vertical', vertical: 'either' };
      return { ...row, grainDirection: grainCycle[row.grainDirection] };
    }
    return row;
  });
  setStockSheets(updated);
};

/**
 * Add a new stock sheet
 * @param {Array} stockSheets - Current stock sheets array
 * @param {Function} setStockSheets - State setter for stock sheets
 * @param {Object} userSubscription - User subscription object
 * @param {Function} setUpgradeModalOpen - State setter for upgrade modal
 */
export const addStock = (stockSheets, setStockSheets, userSubscription, setUpgradeModalOpen) => {
  // Check stock sheet limit for free users
  if (userSubscription?.tier === 'free' && stockSheets.length >= 5) {
    alert('Free tier is limited to 5 stock sheets per project. Upgrade to Premium for unlimited sheets!');
    setUpgradeModalOpen(true);
    return;
  }
  
  // Check if there's already an empty stock sheet (width, length, and quantity are all empty)
  const hasEmptyStock = stockSheets.some(stock => 
    (!stock.width || stock.width === '') && 
    (!stock.length || stock.length === '') && 
    (!stock.quantity || stock.quantity === '')
  );
  
  // Only add a new stock sheet if there's no empty stock sheet already
  if (!hasEmptyStock) {
    setStockSheets([...stockSheets, { width: '', length: '', quantity: '', materialType: '', enabled: true, grainDirection: 'either' }]);
  }
};

/**
 * Remove a stock sheet
 * @param {number} idx - Stock sheet index
 * @param {Array} stockSheets - Current stock sheets array
 * @param {Function} setStockSheets - State setter for stock sheets
 */
export const removeStock = (idx, stockSheets, setStockSheets) => {
  setStockSheets(stockSheets.filter((_, i) => i !== idx));
};

